import { Table, Button, Modal, Form, Input, InputNumber, message, Popconfirm, Space, Tag, Checkbox, Divider, Spin, Alert } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

function AdminClients() {
  const { t } = useTranslation()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editRecord, setEditRecord] = useState(null)
  const [databases, setDatabases] = useState([])
  const [testingERP, setTestingERP] = useState(false)
  const [selectedDatabases, setSelectedDatabases] = useState([])
  const [form] = Form.useForm()

  const watchedWhatsapp = Form.useWatch('whatsapp_enabled', form)
  const watchedDashboard = Form.useWatch('dashboard_enabled', form)
  const watchedIncomeStatement = Form.useWatch('income_statement_enabled', form)
  const noFeatureSelected = !watchedWhatsapp && !watchedDashboard && !watchedIncomeStatement

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: `Bearer ${token}` }

  const fetchClients = async () => {
    setLoading(true)
    try {
      const res = await axios.get('/api/admin/clients', { headers })
      setClients(res.data)
    } catch (err) {
      message.error(t('adminClients.fetchError'))
    }
    setLoading(false)
  }

  useEffect(() => { fetchClients() }, [])

  // اختبار الاتصال بـ ERP
  const testERPConnection = async () => {
    const values = form.getFieldsValue(['erp_host', 'erp_port', 'erp_username', 'erp_password'])

    if (!values.erp_host || !values.erp_username || !values.erp_password) {
      message.warning(t('adminClients.erpFillFirst'))
      return
    }

    setTestingERP(true)
    setDatabases([])

    try {
      const res = await axios.post(
        '/api/admin/clients/test-erp',
        {
          host: values.erp_host,
          port: values.erp_port || 1433,
          username: values.erp_username,
          password: values.erp_password
        },
        { headers }
      )

      if (res.data.success) {
        setDatabases(res.data.databases)
        message.success(t('adminClients.erpConnectSuccess', { count: res.data.databases.length }))
      }
    } catch (err) {
      message.error(err.response?.data?.error || t('adminClients.erpConnectFail'))
    }
    setTestingERP(false)
  }

  const saveClient = async (values) => {
    if (!values.whatsapp_enabled && !values.dashboard_enabled && !values.income_statement_enabled) {
      message.error(t('adminClients.atLeastOneFeatureError'))
      return
    }
    setSaving(true)
    try {
      const data = { ...values, allowed_databases: selectedDatabases }

      if (editRecord) {
        await axios.put(`/api/admin/clients/${editRecord.id}`, data, { headers })
        message.success(t('adminClients.editSuccess'))
      } else {
        await axios.post('/api/admin/clients', data, { headers })
        message.success(t('adminClients.addSuccess'))
      }
      setModal(false)
      form.resetFields()
      setDatabases([])
      setSelectedDatabases([])
      fetchClients()
    } catch (err) {
      message.error(err.response?.data?.error || t('adminClients.genericError'))
    }
    setSaving(false)
  }

  const deleteClient = async (id) => {
    try {
      await axios.delete(`/api/admin/clients/${id}`, { headers })
      message.success(t('adminClients.deleteSuccess'))
      fetchClients()
    } catch (err) {
      message.error(t('adminClients.deleteError'))
    }
  }

  const openEdit = (record) => {
    setEditRecord(record)
    form.setFieldsValue({
      name: record.name,
      email: record.email,
      phone: record.phone,
      subdomain: record.subdomain,
      user_name: record.user_name,
      user_email: record.user_email,
      erp_host: record.host,
      erp_port: record.port || 1433,
      erp_username: record.username,
      whatsapp_enabled: record.whatsapp_enabled,
      dashboard_enabled: record.dashboard_enabled,
      income_statement_enabled: record.income_statement_enabled,
    })
    setSelectedDatabases(record.allowed_databases || [])
    setModal(true)
  }

  const columns = [
    { title: t('adminClients.columnName'), dataIndex: 'name', key: 'name' },
    { title: t('adminClients.columnEmail'), dataIndex: 'email', key: 'email' },
    { title: t('adminClients.columnPhone'), dataIndex: 'phone', key: 'phone' },
    {
      title: t('adminClients.columnSubdomain'),
      dataIndex: 'subdomain',
      key: 'subdomain',
      render: (sub) => sub
        ? <Tag color="blue">{sub}.resortapp.app</Tag>
        : <Tag color="red">{t('adminClients.subdomainUndefined')}</Tag>
    },
    {
      title: t('adminClients.columnErp'),
      dataIndex: 'host',
      key: 'host',
      render: (host, record) => host
        ? <Tag color="green">{t('adminClients.erpConnected', { count: record.allowed_databases?.length || 0 })}</Tag>
        : <Tag color="orange">{t('adminClients.erpNotConfigured')}</Tag>
    },
    {
      title: t('adminClients.columnFeatures'),
      key: 'features',
      render: (_, record) => (
        <Space>
          {record.whatsapp_enabled && <Tag color="green">{t('adminClients.featureWhatsappTag')}</Tag>}
          {record.dashboard_enabled && <Tag color="purple">{t('adminClients.featureDashboardTag')}</Tag>}
          {record.income_statement_enabled && <Tag color="gold">{t('adminClients.featureIncomeStatementTag')}</Tag>}
        </Space>
      )
    },
    {
      title: t('adminClients.columnStatus'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? t('adminClients.statusActive') : t('adminClients.statusInactive')}</Tag>
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('adminClients.deleteConfirmTitle')}
            okText={t('common.yes')} cancelText={t('common.no')}
            onConfirm={() => deleteClient(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>{t('common.delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>{t('adminClients.pageTitle')}</h2>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => {
            setEditRecord(null)
            form.resetFields()
            // نقترح رقم المنفذ التالي تلقائياً = أعلى منفذ مستخدَم حالياً
            // بين كل العملاء + 1 — بدل افتراض ثابت (1433) دائماً، الذي
            // يتصادم فوراً مع عميل موجود أصلاً على نفس المنفذ
            const usedPorts = clients.map(c => Number(c.port)).filter(p => !isNaN(p) && p > 0)
            const nextPort = usedPorts.length > 0 ? Math.max(...usedPorts) + 1 : 1433
            form.setFieldsValue({ erp_port: nextPort, whatsapp_enabled: true, dashboard_enabled: false, income_statement_enabled: false })
            setDatabases([])
            setSelectedDatabases([])
            setModal(true)
          }}>
          {t('adminClients.addClient')}
        </Button>
      </div>

      <Table columns={columns} dataSource={clients} loading={loading} rowKey="id" />

      <Modal
        title={editRecord ? t('adminClients.modalEditTitle') : t('adminClients.modalAddTitle')}
        open={modal}
        onCancel={() => { setModal(false); form.resetFields(); setDatabases([]); setSelectedDatabases([]) }}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={saveClient} layout="vertical" initialValues={{ erp_port: 1433 }}>

          <Divider>{t('adminClients.sectionCompanyInfo')}</Divider>

          <Form.Item name="name" label={t('adminClients.companyNameLabel')}
            rules={[{ required: true, message: t('adminClients.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t('adminClients.emailLabel')}
            rules={[{ required: true }, { type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t('adminClients.phoneLabel')}
            rules={[{ required: true, message: t('adminClients.phoneRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="subdomain" label={t('adminClients.subdomainLabel')}
            rules={[{ required: true, message: t('adminClients.subdomainRequired') }]}>
            <Input addonAfter=".resortapp.app" placeholder="client1" />
          </Form.Item>

          <Divider>{t('adminClients.sectionLoginAccount')}</Divider>

          <Divider orientation="left" plain>{t('adminClients.sectionFeatures')}</Divider>

          <Space size="large" style={{ marginBottom: 8 }}>
            <Form.Item name="whatsapp_enabled" valuePropName="checked" noStyle>
              <Checkbox>{t('adminClients.featureWhatsapp')}</Checkbox>
            </Form.Item>
            <Form.Item name="dashboard_enabled" valuePropName="checked" noStyle>
              <Checkbox>{t('adminClients.featureDashboard')}</Checkbox>
            </Form.Item>
            <Form.Item name="income_statement_enabled" valuePropName="checked" noStyle>
              <Checkbox>{t('adminClients.featureIncomeStatement')}</Checkbox>
            </Form.Item>
          </Space>

          {noFeatureSelected && (
            <Alert
              type="warning"
              showIcon
              message={t('adminClients.noFeatureWarning')}
              style={{ marginBottom: 16 }}
            />
          )}

          <Divider />

          <Form.Item name="user_name" label={t('adminClients.userNameLabel')}
            rules={[{ required: !editRecord, message: t('adminClients.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="user_email" label={t('adminClients.userEmailLabel')}
            rules={[{ required: !editRecord }, { type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="user_password" label={t('adminClients.userPasswordLabel')}
            rules={[{ required: !editRecord, message: t('changePassword.currentPasswordRequired') }]}>
            <Input.Password placeholder={editRecord ? t('adminClients.passwordEditPlaceholder') : ''} />
          </Form.Item>

          <Divider>{t('adminClients.sectionErp')}</Divider>

          <Form.Item name="erp_host" label={t('adminClients.erpHostLabel')}>
            <Input placeholder="localhost" />
          </Form.Item>
          <Form.Item
            name="erp_port"
            label={t('adminClients.erpPortLabel')}
            tooltip={t('adminClients.erpPortTooltip')}
          >
            <InputNumber style={{ width: '100%' }} min={1} max={65535} placeholder="1433" />
          </Form.Item>
          <Form.Item name="erp_username" label={t('adminClients.erpUsernameLabel')}>
            <Input placeholder="sa" />
          </Form.Item>
          <Form.Item name="erp_password" label={t('adminClients.erpPasswordLabel')}>
            <Input.Password placeholder={editRecord ? t('adminClients.erpPasswordEditPlaceholder') : ''} />
          </Form.Item>

          <Button
            icon={<ApiOutlined />}
            onClick={testERPConnection}
            loading={testingERP}
            style={{ marginBottom: 16 }}
            block
          >
            {t('adminClients.testConnection')}
          </Button>

          {testingERP && (
            <div style={{ textAlign: 'center', margin: 16 }}>
              <Spin /> {t('adminClients.testingConnection')}
            </div>
          )}

          {databases.length > 0 && (
            <Form.Item label={t('adminClients.chooseAllowedDatabases')}>
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                value={selectedDatabases}
                onChange={setSelectedDatabases}
                options={databases.map(db => ({ label: db, value: db }))}
              />
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit" block loading={saving} disabled={noFeatureSelected} style={{ marginTop: 16 }}>
            {editRecord ? t('adminClients.saveEdit') : t('adminClients.addSubmit')}
          </Button>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminClients
